import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-client Google Ads connection panel.
 * Mirrors the existing ClientGoogleAnalytics pattern: shows "Not configured"
 * when the global developer token isn't set, then the OAuth flow + account
 * selector once super_admin authenticates.
 *
 * Props:
 *   - clientId
 *   - isSuperAdmin (write disabled when false)
 */
export default function ClientGoogleAds({ clientId, isSuperAdmin = true }) {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/google-ads/status`);
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
      setErr(e.response?.data?.detail || e.message);
    }
  }, [clientId]);

  useEffect(() => {
    load();
    // Catch redirect flag from OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get("gads_connected") === "1") {
      setMsg("Google Ads connected. Now pick the account to link.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (params.get("gads_error")) {
      setErr(`OAuth error: ${params.get("gads_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  const connect = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/google-ads/auth-url`);
      window.location.href = res.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const fetchAccounts = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const res = await axios.get(`${API}/clients/${clientId}/google-ads/accounts`);
      setAccounts(res.data.accounts || []);
      if (!res.data.accounts?.length) setMsg("No accessible accounts returned.");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const selectAccount = async (acc) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      await axios.put(`${API}/clients/${clientId}/google-ads/select-account`, {
        account_id: acc.account_id,
        login_customer_id: acc.login_customer_id,
        account_name: acc.name,
      });
      setMsg(`Linked: ${acc.name} (${acc.account_id})`);
      setAccounts([]);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Ads for this client?")) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await axios.post(`${API}/clients/${clientId}/google-ads/disconnect`);
      setMsg("Disconnected.");
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!status) {
    return <div className="card-flat p-4 text-[11px] text-[#71717A]">Loading Google Ads status…</div>;
  }

  const tokenMissing = !status.configured;
  const connected = status.connected;
  const linked = status.account_linked;

  return (
    <div className="card-flat p-5 space-y-3" data-testid="client-google-ads-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Google Ads</h3>
          <p className="text-[11px] text-[#71717A]">
            {tokenMissing
              ? "Integration not configured yet — super-admin must add GOOGLE_ADS_DEVELOPER_TOKEN to backend/.env."
              : !connected
                ? "Connect this client's Google Ads account to pull campaigns + spend + AI insights."
                : !linked
                  ? "Authenticated. Pick the account to link."
                  : `Linked: ${status.account_name || status.customer_id}`}
          </p>
        </div>
        <span
          className="text-[10px] px-2 py-0.5 rounded font-medium uppercase tracking-wider"
          style={{
            background: linked ? "#10B98122" : connected ? "#F59E0B22" : tokenMissing ? "#52525B22" : "#FF444422",
            color: linked ? "#6EE7B7" : connected ? "#FCD34D" : tokenMissing ? "#A1A1AA" : "#FF8080",
          }}
          data-testid="gads-status-pill"
        >
          {linked ? "Linked" : connected ? "Account pending" : tokenMissing ? "Setup pending" : "Not connected"}
        </span>
      </div>

      {!isSuperAdmin && (
        <p className="text-[10px] text-[#71717A] italic">Super-admin only — contact your agency to connect.</p>
      )}

      {isSuperAdmin && !tokenMissing && !connected && (
        <button type="button" onClick={connect} disabled={busy} className="btn-primary text-xs" data-testid="gads-connect-btn">
          {busy ? "Redirecting…" : "Connect with Google"}
        </button>
      )}

      {isSuperAdmin && connected && !linked && (
        <div className="space-y-2">
          <button type="button" onClick={fetchAccounts} disabled={busy} className="btn-secondary text-xs" data-testid="gads-fetch-accounts-btn">
            {busy ? "Fetching…" : "Fetch accessible accounts"}
          </button>
          {accounts.length > 0 && (
            <div className="border border-[#2A2A2A] rounded max-h-60 overflow-auto" data-testid="gads-accounts-list">
              {accounts.map((a) => (
                <button type="button" key={a.account_id}
                  onClick={() => selectAccount(a)}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 border-b border-[#2A2A2A] last:border-b-0 hover:bg-[#1A1A1A] text-xs flex items-center justify-between"
                  data-testid={`gads-account-${a.account_id}`}>
                  <span>
                    <span className="font-medium">{a.name}</span>
                    <span className="text-[#71717A] ml-2">{a.account_id}{a.is_manager ? " · MCC" : ""}</span>
                  </span>
                  <span className="text-[10px] text-[#A1A1AA]">{a.login_customer_id ? `via ${a.login_customer_id}` : "direct"}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {isSuperAdmin && linked && (
        <div className="flex items-center gap-2">
          <button type="button" onClick={disconnect} disabled={busy} className="text-[11px] px-2 py-1 rounded border border-[#FF444444] text-[#FF8080] hover:bg-[#FF444411]" data-testid="gads-disconnect-btn">
            Disconnect
          </button>
          <span className="text-[10px] text-[#71717A]">
            {status.login_customer_id ? `MCC ${status.login_customer_id} → ` : ""}{status.customer_id}
          </span>
        </div>
      )}

      {msg && <p className="text-[11px] text-[#6EE7B7]" data-testid="gads-msg">{msg}</p>}
      {err && <p className="text-[11px] text-[#FF8080]" data-testid="gads-err">{err}</p>}
    </div>
  );
}
